import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import FilterBar from './components/FilterBar';
import KPICard from './components/KPICard';
import ReportChart from './components/ReportChart';
import ReportAnalysis from './components/ReportAnalysis';
import ExportButton from './components/ExportButton';
import { getSubcontractingReports } from '../../../services/reportService';
import { CHART_COLORS } from './utils/chartConfig';
import { prepareKPIsForExport } from './utils/exportUtils';
import './Reports.css';

const SubcontractingReports = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({});
    const [reportData, setReportData] = useState(null);

    const fetchReportData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getSubcontractingReports(filters);
            setReportData(response.data);
        } catch (err) {
            console.error('Error fetching subcontracting reports:', err);
            setError('Erreur lors du chargement des données. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    const costBreakdownData = reportData?.costBreakdown ? {
        labels: ['CA affrété', 'Coût sous-traitance', 'Marge brute'],
        datasets: [{
            data: [
                reportData.costBreakdown.charteredRevenue,
                reportData.costBreakdown.subcontractingCost,
                reportData.costBreakdown.grossMargin
            ],
            backgroundColor: [CHART_COLORS.primary, CHART_COLORS.danger, CHART_COLORS.success],
            borderWidth: 0
        }]
    } : null;

    const volumeBySubcontractorData = reportData?.volumeBySubcontractor ? {
        labels: reportData.volumeBySubcontractor.map(item => item.name),
        datasets: [{
            label: 'Volume',
            data: reportData.volumeBySubcontractor.map(item => item.volume),
            backgroundColor: CHART_COLORS.secondary,
            borderRadius: 8
        }]
    } : null;

    const exportData = reportData ? [
        ...prepareKPIsForExport(reportData.kpis),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'Volume par Sous-traitant', Valeur: '' },
        ...(reportData.volumeBySubcontractor || []).map(item => ({ Indicateur: item.name, Valeur: item.volume }))
    ] : null;

    return (
        <DashboardLayout>
            <div className="reports-page">
                <div className="reports-header">
                    <div>
                        <h1 className="reports-title">Rapports & Analytics</h1>
                        <p className="reports-subtitle">Rapports de sous-traitance</p>
                    </div>
                    <ExportButton
                        reportName="rapports_sous_traitance"
                        data={exportData}
                        headers={['Indicateur', 'Valeur']}
                        keys={['Indicateur', 'Valeur']}
                        disabled={loading || !reportData}
                    />
                </div>

                <FilterBar
                    onFilterChange={setFilters}
                    availableFilters={['currency', 'period', 'status', 'subcontractor']}
                />

                {error && (
                    <div className="alert alert-danger rounded-3 border-0 shadow-sm">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                )}

                {/* Analysis Section */}
                {reportData?.analysis && (
                    <ReportAnalysis analysis={reportData.analysis} />
                )}

                <div className="row g-4 mb-4">
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="CA affrété"
                            value={reportData?.kpis?.charteredRevenue}
                            previousValue={reportData?.kpis?.previousCharteredRevenue}
                            format="currency"
                            currency={filters.currency || 'EUR'}
                            icon="bi bi-currency-euro"
                            gradient="blue"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Coût sous-traitance"
                            value={reportData?.kpis?.subcontractingCost}
                            previousValue={reportData?.kpis?.previousSubcontractingCost}
                            format="currency"
                            currency={filters.currency || 'EUR'}
                            icon="bi bi-cash-stack"
                            gradient="red"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Marge brute"
                            value={reportData?.kpis?.grossMargin}
                            previousValue={reportData?.kpis?.previousGrossMargin}
                            format="currency"
                            currency={filters.currency || 'EUR'}
                            icon="bi bi-graph-up-arrow"
                            gradient="green"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Taux de marge"
                            value={reportData?.kpis?.marginRate}
                            format="percentage"
                            icon="bi bi-percent"
                            gradient="purple"
                            loading={loading}
                            decimals={1}
                        />
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-lg-6">
                        <ReportChart
                            type="doughnut"
                            data={costBreakdownData}
                            title="Répartition des coûts"
                            height={350}
                            loading={loading}
                            id="cost-breakdown-chart"
                            options={{ dataType: 'currency' }}
                        />
                    </div>
                    <div className="col-lg-6">
                        <ReportChart
                            type="bar"
                            data={volumeBySubcontractorData}
                            title="Volume par sous-traitant"
                            height={350}
                            loading={loading}
                        />
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default SubcontractingReports;
